import React, { useState } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInvoice } from '../context/InvoiceContext';
import { useAuth } from '../context/AuthContext';

const { FiFileText, FiList, FiSettings, FiMenu, FiX, FiUsers, FiPackage, FiImage, FiGlobe, FiBriefcase, FiCompass, FiLogOut } = FiIcons;

function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useInvoice();
  const { logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { id: 'create', name: 'Create Invoice', icon: FiFileText, path: '/' },
    { id: 'marketing', name: 'Marketing Invoice', icon: FiBriefcase, path: '/marketing' },
    { id: 'travel', name: 'Travel Invoice', icon: FiGlobe, path: '/travel' },
    { id: 'excursion', name: 'Excursion Booking', icon: FiCompass, path: '/excursion' },
    { id: 'invoices', name: 'Invoice List', icon: FiList, path: '/invoices' },
    { id: 'customers', name: 'Customers', icon: FiUsers, path: '/customers' },
    { id: 'products', name: 'Products', icon: FiPackage, path: '/products' },
    { id: 'settings', name: 'Settings', icon: FiSettings, path: '/settings' },
  ];

  const activeTab = navigation.find(item => location.pathname === '/' ? item.path === '/' : location.pathname.startsWith(item.path) && item.path !== '/')?.id || 'invoices';
  const pageTitle = navigation.find(item => item.id === activeTab)?.name || 'Details';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <motion.div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} initial={{ x: -256 }} animate={{ x: 0 }} transition={{ duration: 0.3 }}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              {settings.logoUrl ? <img src={settings.logoUrl} alt="Company Logo" className="w-8 h-8 object-contain" /> : <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center"><SafeIcon icon={FiImage} className="w-4 h-4 text-gray-400" /></div>}
              <h1 className="text-xl font-bold text-gray-900">InvoiceGen</h1>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} className="w-6 h-6" /></button>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item) => (
              <motion.a key={item.id} href={`#${item.path}`} onClick={() => setSidebarOpen(false)} className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === item.id ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} whileHover={{ x: 4 }} whileTap={{ scale: 0.98 }}>
                <SafeIcon icon={item.icon} className="w-5 h-5 mr-3" />
                {item.name}
              </motion.a>
            ))}
          </nav>
          <div className="px-4 py-4 border-t border-gray-200">
            <motion.button onClick={handleLogout} className="flex w-full items-center px-4 py-3 text-sm font-medium rounded-lg text-red-600 hover:bg-red-50" whileHover={{ x: 4 }} whileTap={{ scale: 0.98 }}>
              <SafeIcon icon={FiLogOut} className="w-5 h-5 mr-3" />
              Logout
            </motion.button>
          </div>
        </div>
      </motion.div>
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-white border-b border-gray-200 px-4 py-4 lg:px-6">
          <div className="flex items-center justify-between">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"><SafeIcon icon={FiMenu} className="w-6 h-6" /></button>
            <h2 className="text-2xl font-bold text-gray-900">{pageTitle}</h2>
            <div className="w-10 lg:w-0" />
          </div>
        </div>
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;